<template>
  <div class="modal fade" :id="id" data-backdrop="static" tabindex="-1" role="dialog" aria-labelledby="staticBackdrop" aria-hidden="true">
    <div class="modal-dialog modal-lg" role="document">
      <form class="form" @submit.prevent="submitData">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('projects.fiches.add_info_title') }}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="resetData">
                <span class="svg-icon svg-icon-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                          fill="currentColor" />
                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                          fill="currentColor" />
                  </svg>
                </span>
            </div>
          </div>

          <div class="modal-body">
            <RequiredText />
            <div v-for="(info, index) in formInfoData.details" :key="index">
              <div class="row mb-5 d-flex align-items-center" v-for="(data, key) in info.data" :key="key">
                <Input
                  container-class="col-12 col-md-5 form-group"
                  :name="`info_name_`+id+index+key"
                  type="text"
                  :label="$t('projects.fiches.info')"
                  labelClass="form-label fs-6 text-black"
                  :inputClass="'form-control form-control-lg form-control-solid'"
                  size="lg"
                  :placeholder="$t('projects.fiches.info_placeholder')"
                  :isRequired="true" 
                  v-model="data.key"
                  :wishlistId="'listWish'+id+index+key"
                  :wishlists="filteredOtherInformations(index, key, data.key)"
                />
                <Input
                  container-class="col-12 col-md-6 form-group"
                  :name="`info_value_`+id+index+key"
                  type="text"
                  :label="$t('projects.fiches.value')"
                  labelClass="form-label fs-6 text-black"
                  :inputClass="'form-control form-control-lg form-control-solid'"
                  size="lg"
                  :placeholder="$t('projects.fiches.info_value_placeholder')"
                  :isRequired="true" 
                  v-model="data.value"
                />
                <div class="col-12 col-md-1 d-flex justify-content-end" style="margin-top: 20px;">
                  <div :style="{'border': '1px solid '+ $getCssVariable('--bs-danger') }" class="btn btn-icon btn-icon-muted btn-active-light w-40px h-40px" type="button" @click="removeInfo(index)">
                    <i class="fas fa-trash fs-3 text-danger"></i>
                  </div>
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-end">
              <div :style="{'border': '1px solid '+ $getCssVariable('--bs-primary') }" class="btn btn-md btn-active-light" type="button" @click="addInfo">
                <i class="fas fa-plus fs-3 text-primary me-3"></i> <span class="text-primary">{{ $t('projects.fiches.add_info') }}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <Button type="cancel" @click="resetData" :id="id+'_close_btn'" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.close"></Button>
            <Button type="submit" :id="id+'_save_btn'" btn-class="btn-sm me-3" size="7px 30px" btn-text="projects.fiches.generate" :isLoading="isLoadingInfo"></Button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import {ref, defineProps, computed} from 'vue'
  import Button from "@/components/Button.vue"
  import Input from "@/components/Input.vue";
  import RequiredText from "@/components/RequiredText.vue";
  import { useStore } from "vuex"
  import { useRoute, useRouter } from "vue-router"

  const store = useStore()
  const route = useRoute()
  const router = useRouter()

  const emits = defineEmits(['load'])

  const props = defineProps({
    id: {
      type: String,
      required: true
    },
    fiche: {
      type: Object,
      default: {}
    }
  })

  const formInfoData = ref({
    project_id: '',
    summary_id: '',
    details: [
      {
        is_generated: true,
        field: null,
        data: [
          {
            key: '',
            value: ''
          }
        ]
      }
    ]
  })
  const isLoadingInfo = ref(false)

  const ficheDetailData = computed(() => {
    return (props.fiche?.details ?? []).map((item) => {
      let field = item.field
      return { ...item, field: field, id: field }
    })
  })

  const otherInformations = computed(() => {
    return [
      i18n.global.t('projects.fiches.ifu'),
      i18n.global.t('projects.fiches.capital'),
      i18n.global.t('projects.fiches.rccm'),
      i18n.global.t('projects.fiches.address_siege'),
      i18n.global.t('projects.fiches.estate_label'),
      i18n.global.t('projects.fiches.estate_content'),
      i18n.global.t('projects.fiches.guarantee'),
      i18n.global.t('projects.fiches.amount')
    ];
  })

  const infoNotExists = computed(() => {
    let details = []
    ficheDetailData.value?.forEach((detail) => {
      detail.data?.forEach((d) => {
        if (d.key) details.push(d.key);
      });
    })

    return otherInformations.value?.filter(item =>
      !details.includes(item)
    );
  })

  function filteredOtherInformations(currentIndex, currentKeyIndex, currentValue) {
    let selectedKeys = []
    formInfoData.value.details?.forEach((detail, detailIndex) => {
      detail.data?.forEach((d, dataKey) => {
        if (!(detailIndex === currentIndex && dataKey === currentKeyIndex)) {
          if (d.key) selectedKeys.push(d.key);
        }
      });
    })
    return infoNotExists.value?.filter(item =>
      !selectedKeys.includes(item) || item === currentValue
    );
  }

  function reset(){
    formInfoData.value = {
      project_id: '',
      summary_id: '',
      details: [
        {
          is_generated: true,
          field: null,
          data: [
            {
              key: '',
              value: ''
            }
          ]
        }
      ]
    }
  }

  function resetData() {
    closeModal()
    reset()
  }
  
  function addInfo(){
    formInfoData.value.details.push({
      is_generated: true,
      field: null,
      data: [
        {
          key: '',
          value: ''
        }
      ]
    })
  }

  function removeInfo(key){
    if(formInfoData.value.details?.length > 1){
      formInfoData.value.details.splice(key, 1);
    }
  }

  function submitData() {
    isLoadingInfo.value = true
    formInfoData.value.project_id = route.params.id
    formInfoData.value.summary_id = props.fiche?.id 
    const details = [
      ...ficheDetailData.value,
      ...(formInfoData.value.details ?? [])
    ]

    let action = props.fiche?.id  ? 'projects/updateProjectSummary' : 'projects/storeProjectSummary'

    store.dispatch(action, {...formInfoData.value, details: details})
        .then(res => {
          emits('load')
          router.replace({ query: { ...route.query, detail: 'generate' } });
          window.toastr["success"](i18n.global.t('notification.fiche_generated'));
          resetData()
        })
        .catch()
        .finally(() => isLoadingInfo.value = false)
  }

  function closeModal(){
    $('#'+props.id).modal('toggle')
  }
</script>